import React, { useEffect, useState } from 'react';

import { Col, Divider, Form, Input, Modal, notification, Row, Select } from 'antd';

import dayjs from 'dayjs';
import { makeFilterOption } from 'lib/helpers/Form.helper';

import API from '../../lib/API';
import { createReserve } from '../../services/Reserve.service';
import { listResident } from '../../services/ResidentService';
import { ApartmentInterface } from '../_Common/_Interfaces/Apartment';
import { ReserveInterface } from '../_Common/_Interfaces/Reserve';

const filterOption = makeFilterOption();

type Props = {
    bookingDate: dayjs.Dayjs | null,
    reserve: ReserveInterface;
    apartments: Array<ApartmentInterface>;
    ambients: Ambient.SimpleModel[];
    isVisible: boolean;
    hideModalForm: Function;
};

const FormReserve: React.FC<Props> = ({ bookingDate, reserve, apartments, ambients, isVisible, hideModalForm }) => {
    const [saving, setSaving] = useState(false);
    const [residents, setResidents] = useState<Resident.Model[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [form] = Form.useForm();

    const watchedApartmentId = Form.useWatch('apartmentId', form);

    const onCancel = () => hideModalForm();

    const saveItem = async (reserve: ReserveInterface) => {
        setSaving(true);

        if (reserve.id) {
            await API.put(`/reserve/${reserve.id}`, reserve);
        } else {
            const body = {
                ambientId: reserve.ambientId,
                apartmentId: reserve.apartmentId,
                resident_id: reserve.resident_id,
                bookingDate: reserve.bookingDate.format('YYYY/MM/DD'),
            };

            const response = await createReserve(body);

            if (!response) {
                notification.warning({ message: 'Atenção', description: 'Este ambiente já foi reservado nesta data.' });
            } else if ('status' in response) {
                notification.error({ message: 'Erro', description: response.message });
            } else {
                notification.success({ message: 'Sucesso', description: 'Reserva realizada com sucesso' });
            }
        }

        setSaving(false);
        hideModalForm(true);
    };

    const onOk = async () => {
        const values = await form.validateFields();
        await saveItem(values);
        form.resetFields();
    };

    const fetchResidents = async () => {
        setIsLoading(true);
        const response = await listResident();

        setResidents(response.filter(resident => resident.apartmentId === watchedApartmentId));
        setIsLoading(false);
    };

    useEffect(() => {
        fetchResidents();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [watchedApartmentId]);

    return (
        <Modal
            centered
            title={reserve.id ? 'Editar reserva' : 'Nova reserva'}
            destroyOnClose={true}
            open={isVisible}
            confirmLoading={saving}
            onOk={onOk}
            onCancel={onCancel}
            okText="Salvar"
        >
            <Form form={form} layout="vertical" initialValues={reserve} onFinish={onOk}>
                <Divider />
                <Row>
                    <Col span={24}>
                        <Form.Item name="apartmentId" label="Apartamento" rules={[{ required: true, message: 'Selecione um apartamento' }]} >
                            <Select
                                allowClear
                                showSearch
                                filterOption={filterOption}
                            >
                                {apartments.map((apartment: ApartmentInterface, key: number) =>
                                    <Select.Option key={key} value={apartment.id}>
                                        {`${apartment.tower.name} ${apartment.name}`}
                                    </Select.Option>)}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col span={24}>
                        <Form.Item name="resident_id" label="Morador" rules={[{ required: true, message: 'Selecione um morador' }]} >
                            <Select
                                showSearch
                                filterOption={filterOption}
                                loading={isLoading}
                                disabled={isLoading}
                            >
                                {residents.map((resident, key) =>
                                    <Select.Option key={key} value={resident.id}>
                                        {resident.name}
                                    </Select.Option>)}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col span={24}>
                        <Form.Item name="ambientId" label="Ambiente" rules={[{ required: true, message: 'Selecione um ambiente' }]} >
                            <Select
                                showSearch
                                filterOption={filterOption}
                            >
                                {ambients.map((ambient, key) =>
                                    <Select.Option key={key} value={ambient.id}>
                                        {ambient.name}
                                    </Select.Option>)}
                            </Select>
                        </Form.Item>
                    </Col>
                </Row>
                <Form.Item name="bookingDate" initialValue={bookingDate}>
                    <Input type="hidden" />
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default FormReserve;
